import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../variables/api/axiosInstance";
import { dedupeParametresByLibelle } from "../variables/functions";

/**
 * Référentiels des formulaires ENGIN — source unique pour la création de contrat
 * ET pour les avenants (Renouvellement, Incorporation, Changement de véhicule,
 * Avenant d'ordre engin), où ce chargement était dupliqué et divergent.
 *
 * GENRE / CARROSSERIE
 *   Liste PROPRE à la catégorie (`/api/parametre/type/{X}/categorie/{catCode}`), avec
 *   REPLI sur la liste globale si la catégorie n'en a aucune. Ce repli est essentiel :
 *   le backend applique désormais une sémantique « override » (les paramètres de la
 *   catégorie MASQUENT les globaux) — sans repli, une catégorie non configurée
 *   afficherait un Select VIDE sur un champ requis. Tout est dédupliqué par libellé
 *   (la base contient des doublons : TRICYCLE, VELOMOTEUR…).
 *
 * MARQUES
 *   Référentiel `/api/vehicule-marques` → { marqueId, nom, modeles: [{ nom }] }.
 *   C'est le SEUL qui porte les MODÈLES, indispensables à l'AutoComplete du champ
 *   « Type » (cf. TypeAutoComplete). L'ancien `/api/parametre/type/MARQUE` n'en a pas :
 *   c'est pourquoi le Type était en saisie libre dans tous les avenants.
 *   Ce référentiel n'est PAS catégorisé : les marques sont globales (comportement déjà
 *   en vigueur sur la création de contrat), les Select sont en recherche.
 *
 * @param {string} catCode code catégorie du contrat (ex. 'M', 'MO', 'T', 'B', 'A')
 * @returns {{genres: Array, carosseries: Array, marques: Array,
 *            defaultGenre: ?string, defaultCarosserie: ?string}}
 */
export const useEnginReferentiels = (catCode) => {
    const [genres, setGenres] = useState([]);
    const [carosseries, setCarosseries] = useState([]);
    const [marques, setMarques] = useState([]);
    // Listes globales conservées À PART : servent de repli sans jamais laisser en place
    // la liste de la catégorie précédemment sélectionnée.
    const [globalGenres, setGlobalGenres] = useState([]);
    const [globalCarosseries, setGlobalCarosseries] = useState([]);

    // Globales + marques : chargées une seule fois.
    useEffect(() => {
        axiosInstance
            .get(`/api/parametre/type/GENRE`)
            .then((res) => {
                const list = dedupeParametresByLibelle(res.data);
                setGlobalGenres(list);
                // Anti-course : ne JAMAIS écraser une liste « catégorie » déjà arrivée.
                setGenres((prev) => (prev.length ? prev : list));
            })
            .catch((e) => console.log(e));

        axiosInstance
            .get(`/api/parametre/type/CAROSSERIE`)
            .then((res) => {
                const list = dedupeParametresByLibelle(res.data);
                setGlobalCarosseries(list);
                setCarosseries((prev) => (prev.length ? prev : list));
            })
            .catch((e) => console.log(e));

        axiosInstance
            .get(`/api/vehicule-marques`)
            .then((res) => setMarques((res.data || []).filter((m) => m?.nom)))
            .catch((e) => console.log(e));
    }, []);

    // Listes propres à la catégorie (repli global si la catégorie n'a rien).
    useEffect(() => {
        if (!catCode) return;

        axiosInstance
            .get(`/api/parametre/type/GENRE/categorie/${catCode}`)
            .then((res) => {
                const list = Array.isArray(res.data) && res.data.length ? res.data : globalGenres;
                if (list.length) setGenres(dedupeParametresByLibelle(list));
            })
            .catch((e) => console.log(e));

        axiosInstance
            .get(`/api/parametre/type/CAROSSERIE/categorie/${catCode}`)
            .then((res) => {
                const list = Array.isArray(res.data) && res.data.length ? res.data : globalCarosseries;
                if (list.length) setCarosseries(dedupeParametresByLibelle(list));
            })
            .catch((e) => console.log(e));
    }, [catCode, globalGenres, globalCarosseries]);

    // Valeur à pré-remplir quand la catégorie n'offre qu'UNE option (ex. TPV → VOITURE).
    // L'écran appelant décide de l'appliquer (uniquement sur un champ encore vide).
    const defaultGenre = useMemo(
        () => (genres.length === 1 ? genres[0]?.libelleParametre : null),
        [genres]
    );
    const defaultCarosserie = useMemo(
        () => (carosseries.length === 1 ? carosseries[0]?.libelleParametre : null),
        [carosseries]
    );

    return { genres, carosseries, marques, defaultGenre, defaultCarosserie };
};

export default useEnginReferentiels;
